/**
 * Helpers for the product card strip Pebbl can put inside a reply.
 *
 * Pure on purpose: the live stream (`component` frames) and the stored transcript
 * (`components` on each message) both go through cardedSkus(), so a reloaded reply cards exactly
 * what the streamed one did, and placeProductCards() holds the placement rule for both chat
 * surfaces.
 */

/** One run of a rendered reply: plain reply text, or one product's entry. */
export interface AiChatReplySegment {
  /** Sanitized HTML, ready for `[innerHTML]`. */
  html: string;
  /** This entry's number in the reply - pairEntries() finds its card by link; null for plain text. */
  entry: number | null;
  /** An entry directly under another entry, which draws the divider between the two. */
  follows_entry: boolean;
}

/** A rendered reply cut into plain text and product entries. */
export interface AiChatReplyLayout {
  segments: AiChatReplySegment[];
  /** How many SKUs found an entry; the rest go in the strip under the settled reply. */
  placed: number;
  /** Each entry's product link, by entry number - what pairEntries() matches the cards on. */
  entry_hrefs: (string | null)[];
  /** What the whole reply names, so the strip only shows products the reply talks about. */
  mentions: AiChatReplyMentions;
}

/** The products a reply refers to anywhere - entries, tables or prose. */
export interface AiChatReplyMentions {
  /** productSlug() of every link in the reply. */
  slugs: string[];
  /** The reply's plain text, normalised by normaliseName(). */
  text: string;
}

/** The fields of a resolved card the pairing reads - structural, so this file stays service-free. */
interface PairableCard {
  name: string;
  url: string;
}

const NO_MENTIONS: AiChatReplyMentions = { slugs: [], text: '' };

/**
 * One carded product: its SKU, the product page the reply links it by (null on old payloads),
 * and whether the frame flags it as the reply's recommendation.
 */
export interface AiChatCardedProduct {
  sku: string;
  link: string | null;
  recommended: boolean;
}

/**
 * Flattens every `product_cards` payload into one product list, in order.
 *
 * Unknown component types, a missing or empty `skus` and non-string entries are dropped rather
 * than treated as errors - a new component type must not break the widget. SKUs are kept exactly
 * as sent: not trimmed, re-cased, sorted or de-duplicated (the service already de-duplicates, and
 * the order is the display order). Each SKU's link and `recommended` flag come from the payload's
 * `products`; a payload sent before `products` existed gives every SKU a null link and no flag.
 */
export function cardedProducts(components: unknown[] | null | undefined): AiChatCardedProduct[] {
  const products: AiChatCardedProduct[] = [];
  if (!Array.isArray(components)) {
    return products;
  }

  for (const component of components) {
    const payload = component as { type?: unknown; skus?: unknown; products?: unknown } | null;
    if (!payload || payload.type !== 'product_cards' || !Array.isArray(payload.skus)) {
      continue;
    }
    const links = new Map<string, string>();
    const recommended = new Set<string>();
    for (const product of Array.isArray(payload.products) ? payload.products : []) {
      const entry = product as { sku?: unknown; product_link?: unknown; recommended?: unknown } | null;
      if (!entry || typeof entry.sku !== 'string') {
        continue;
      }
      if (typeof entry.product_link === 'string' && entry.product_link) {
        links.set(entry.sku, entry.product_link);
      }
      if (entry.recommended === true) {
        recommended.add(entry.sku);
      }
    }
    for (const sku of payload.skus) {
      if (typeof sku === 'string' && sku.length > 0) {
        products.push({ sku, link: links.get(sku) ?? null, recommended: recommended.has(sku) });
      }
    }
  }
  return products;
}

/** The SKUs of cardedProducts(), in the same order. */
export function cardedSkus(components: unknown[] | null | undefined): string[] {
  return cardedProducts(components).map((product) => product.sku);
}

/**
 * Cuts a rendered reply into plain text and product entries. Each entry's picture is found later
 * by its link (see pairEntries()), never by its position.
 *
 * A card goes to the first of these that fits, matched on its `product_link` exactly:
 *
 *  1. A paragraph, heading or list item that OPENS on the card's link - an entry.
 *  2. Otherwise, the first paragraph or bullet that links to it anywhere - a mention, so a reply
 *     that names its pick mid-sentence ("I'd start with [BORDEAUX](url)") still shows it there.
 *  3. Otherwise (the product is only in a table, say) none here: it is left for the strip.
 *
 * Step 1 runs for every card before step 2, so a product named in the opening sentence and again
 * in its own shortlist entry stays beside its entry.
 *
 * An entry's name line takes several shapes, and all of them count:
 *
 *   **[Name](url)**                         the documented form
 *   **1.** [Name](url) **Recommended**       what it actually sends for a ranked shortlist
 *   1. **[Name](url)**                       as a numbered list item
 *   ### 1. [Name](url)                       as a heading
 *
 * Each entry is rebuilt as a name line (`p.md-entry-name`: ordinal, name and any short bold tag
 * after it, which becomes a badge) followed by the product's details, so the picture can sit
 * beside one uniform block whatever shape the reply took. A name paragraph followed directly by
 * a list owns that list too, as it is the product's detail. A mention is not rebuilt: the picture
 * sits beside that one paragraph or bullet as written, and never takes the blocks after it.
 *
 * Only a LINKED name opens an entry, and where the frame sent `links` (each card's
 * `product_link`), only a link whose URL equals one of them exactly. A bold line with no product
 * link over a list - "**Product cost for 100.8 m²**", "**Important considerations**" - is a
 * section heading, not a product, and stays plain text: no picture column, divider or link arrow.
 * A reply stored before `product_link` existed (every link null) keeps the looser rule for
 * entries, and has no mentions - there is no link to match them on.
 *
 * The entry or mention for the product the frame flags `recommended` (index for index with
 * `links`) gets the green "Recommended" badge. The reply text never marks it.
 *
 * At most `skuCount` blocks are taken - there are never more pictures than cards. Works on the
 * sanitized HTML from renderMarkdown, moving nodes that DOMPurify already parsed, so nothing new
 * reaches the DOM beyond three class names. Needs a DOM: on the server the reply is one plain
 * segment (the chat surfaces are browser-only in practice).
 */
export function placeProductCards(
  html: string | null | undefined,
  skuCount: number,
  links: (string | null)[] = [],
  recommended: boolean[] = []
): AiChatReplyLayout {
  const source = html ?? '';
  if (!source) {
    return { segments: [], placed: 0, entry_hrefs: [], mentions: NO_MENTIONS };
  }
  if (skuCount < 1 || typeof document === 'undefined') {
    return { segments: [{ html: source, entry: null, follows_entry: false }], placed: 0, entry_hrefs: [], mentions: NO_MENTIONS };
  }

  const template = document.createElement('template');
  template.innerHTML = source;

  // The frame's product links: an entry must open on one of these exactly. Empty on a reply
  // stored before `product_link` existed, where any product link opens an entry.
  const productLinks = new Set(links.filter((link): link is string => !!link));
  const recommendedLinks = new Set(links.filter((link, index): link is string => !!link && recommended[index] === true));
  const opensEntry = (element: Element): boolean => {
    const href = openingLink(element)?.getAttribute('href');
    return !!href && (!productLinks.size || productLinks.has(href));
  };
  const isRecommended = (element: Element): boolean =>
    recommendedLinks.has(openingLink(element)?.getAttribute('href') ?? '');

  const mentions: AiChatReplyMentions = {
    slugs: Array.from(template.content.querySelectorAll('a'))
      .map((link) => productSlug(link.getAttribute('href')))
      .filter((slug): slug is string => !!slug),
    text: normaliseName(template.content.textContent ?? '')
  };

  const nodes = Array.from(template.content.childNodes);

  // Step 1, for every card: the blocks that open on a product link, and the lists they fold in.
  const entries = new Set<Element>();
  const folded = new Set<Element>();
  for (let index = 0; index < nodes.length && entries.size < skuCount; index++) {
    const node = nodes[index];
    if (!(node instanceof Element)) {
      continue;
    }
    if (/^(P|H[1-6])$/.test(node.tagName) && opensEntry(node)) {
      entries.add(node);
      const next = nodes[index + 1];
      if (next instanceof Element && isList(next) && !hasEntryItems(next, opensEntry)) {
        folded.add(next);
        index++;
      }
      continue;
    }
    if (isList(node)) {
      for (const item of listItems(node)) {
        if (entries.size < skuCount && opensEntry(item)) {
          entries.add(item);
        }
      }
    }
  }

  // Step 2, for each card step 1 left without a block: the first paragraph or bullet - outside
  // the entries and the lists they own - that links to it anywhere.
  const taken = new Set(Array.from(entries).map((block) => openingLink(block)?.getAttribute('href') ?? ''));
  const candidates = nodes.flatMap((node): Element[] => {
    if (!(node instanceof Element) || folded.has(node)) {
      return [];
    }
    if (node.tagName === 'P') {
      return [node];
    }
    return isList(node) ? listItems(node) : [];
  }).filter((block) => !entries.has(block));
  const mentioned = new Map<Element, string>();
  for (const link of productLinks) {
    if (entries.size + mentioned.size >= skuCount) {
      break;
    }
    if (taken.has(link)) {
      continue;
    }
    const block = candidates.find((candidate) => !mentioned.has(candidate) && linksTo(candidate, link));
    if (block) {
      mentioned.set(block, link);
    }
  }

  const segments: AiChatReplySegment[] = [];
  const entryHrefs: (string | null)[] = [];
  let plain: string[] = [];

  // Consecutive plain blocks share one segment, so their margins collapse exactly as before.
  const flush = (): void => {
    if (plain.length) {
      segments.push({ html: plain.join(''), entry: null, follows_entry: false });
      plain = [];
    }
  };
  const followsEntry = (): boolean => {
    const previous = segments[segments.length - 1];
    return !!previous && previous.entry !== null;
  };
  // `href` is what pairEntries() finds the picture by: the link an entry opens on - opensEntry()
  // only lets a block in that way - or the card link a mention was matched on.
  const pushEntry = (entryHtml: string, href: string | null): void => {
    flush();
    segments.push({ html: entryHtml, entry: entryHrefs.length, follows_entry: followsEntry() });
    entryHrefs.push(href);
  };

  for (let index = 0; index < nodes.length; index++) {
    const node = nodes[index];
    if (!(node instanceof Element)) {
      plain.push(escapeText(node.textContent ?? ''));
      continue;
    }

    if (entries.has(node)) {
      let entryHtml = buildEntry(node, null, isRecommended(node));
      const next = nodes[index + 1];
      if (next instanceof Element && folded.has(next)) {
        entryHtml += next.outerHTML;
        index++;
      }
      pushEntry(entryHtml, openingLink(node)?.getAttribute('href') ?? null);
      continue;
    }

    const link = mentioned.get(node);
    if (link) {
      pushEntry(mentionEntry(node.outerHTML, recommendedLinks.has(link)), link);
      continue;
    }

    const items = isList(node) ? listItems(node) : [];
    if (items.some((item) => entries.has(item) || mentioned.has(item))) {
      const first = node.tagName === 'OL' ? (parseInt(node.getAttribute('start') ?? '1', 10) || 1) : null;
      let run: Element[] = [];
      let runStart = 0;
      items.forEach((item, position) => {
        const itemLink = mentioned.get(item);
        if (entries.has(item) || itemLink) {
          if (run.length) {
            plain.push(listOf(node, run, runStart));
            run = [];
          }
          if (itemLink) {
            // Kept as a one-bullet list, so it reads as the bullet it was.
            pushEntry(mentionEntry(listOf(node, [item], position), recommendedLinks.has(itemLink)), itemLink);
          } else {
            pushEntry(buildEntry(item, first === null ? null : first + position, isRecommended(item)),
              openingLink(item)?.getAttribute('href') ?? null);
          }
          return;
        }
        if (!run.length) {
          runStart = position;
        }
        run.push(item);
      });
      if (run.length) {
        plain.push(listOf(node, run, runStart));
      }
      continue;
    }

    plain.push(node.outerHTML);
  }
  flush();

  return { segments, placed: entryHrefs.length, entry_hrefs: entryHrefs, mentions };
}

/**
 * Which card each entry shows, by entry number: the card whose product link the entry links to,
 * each card used once, null where the entry links to none of them.
 *
 * Matched on the link, never the position: the service sends `product_link` for each card, and
 * an entry takes the card whose link equals its own, exactly. A card with no `product_link` (a
 * reply stored before the field existed) falls back to comparing productSlug() of the entry's
 * link with the resolved product's URL. An entry nothing matches gets no picture - a wrong
 * product's picture beside a name is worse than none.
 */
export function pairEntries(
  hrefs: (string | null)[],
  cards: (PairableCard | null)[],
  links: (string | null)[] = []
): (number | null)[] {
  const slugs = cards.map((card) => (card ? productSlug(card.url) : null));
  const taken = new Set<number>();
  return hrefs.map((href) => {
    if (!href) {
      return null;
    }
    const slug = productSlug(href);
    const match = cards.findIndex((card, index) => {
      if (!card || taken.has(index)) {
        return false;
      }
      const link = links[index];
      return link ? link === href : !!slug && slugs[index] === slug;
    });
    if (match < 0) {
      return null;
    }
    taken.add(match);
    return match;
  });
}

/**
 * True where the reply refers to this product - links to it, or names it in the text (a
 * comparison table usually names products without links). A card the reply never mentions is
 * one the agent attached without writing about it, and shown on its own it reads as a stray
 * picture, so the strip leaves it out.
 */
export function isMentioned(card: PairableCard, mentions: AiChatReplyMentions, link: string | null = null): boolean {
  const slug = productSlug(link ?? card.url);
  if (slug && mentions.slugs.includes(slug)) {
    return true;
  }
  const name = normaliseName(card.name);
  return !!name && mentions.text.includes(name);
}

/**
 * The last path segment of a product URL, lower-cased - so `https://gatherco.com.au/sahar/`,
 * `/sahar` and `sahar?ref=chat` all compare equal to a card's `/sahar`.
 */
export function productSlug(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }
  const path = url.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, '').split(/[?#]/)[0];
  const segment = path.split('/').filter(Boolean).pop();
  if (!segment) {
    return null;
  }
  try {
    return decodeURIComponent(segment).toLowerCase();
  } catch {
    return segment.toLowerCase();
  }
}

/** Lower-cased, every dash as `-`, runs of whitespace as one space - so "JAMILA – Honed" finds "jamila - honed". */
function normaliseName(value: string): string {
  return value.toLowerCase().replace(/[\u2010-\u2015]/g, '-').replace(/\s+/g, ' ').trim();
}

function isList(element: Element): boolean {
  return element.tagName === 'UL' || element.tagName === 'OL';
}

function hasEntryItems(list: Element, opensEntry: (element: Element) => boolean): boolean {
  return listItems(list).some(opensEntry);
}

/** A list's own items - not those of a list nested inside one. */
function listItems(list: Element): Element[] {
  return Array.from(list.children).filter((child) => child.tagName === 'LI');
}

/** True where `block` holds a link to exactly `href`, anywhere in it. */
function linksTo(block: Element, href: string): boolean {
  return Array.from(block.querySelectorAll('a')).some((link) => link.getAttribute('href') === href);
}

/**
 * A mention's entry: the paragraph or bullet as the reply wrote it, under the "Recommended" badge
 * when it names the recommended product. Not rebuilt like an entry - the product's name is
 * mid-sentence, so there is no name line to lift out.
 */
function mentionEntry(blockHtml: string, recommended: boolean): string {
  return (recommended ? '<p class="md-entry-tag"><strong class="md-entry-badge">Recommended</strong></p>' : '') + blockHtml;
}

const ORDINAL = /^\s*\d+[.)]\s*$/;
const EMPHASIS = /^(STRONG|B|EM|I)$/;
const BLOCK = /^(UL|OL|P|DIV|DL|TABLE|BLOCKQUOTE|PRE|H[1-6])$/;

/**
 * The link the element opens on, past an optional ordinal ("1." as text or in bold) - bare, or
 * wrapped in bold/italic (`**[Name](url)**`, `**1. [Name](url)**`); null where it opens on
 * anything else. A sentence that only mentions a product ("I'd lead with [Name](url)") starts
 * with text, so it never counts.
 */
function openingLink(element: Element): Element | null {
  let node = firstContent(element.firstChild);
  if (isOrdinal(node)) {
    node = firstContent(node!.nextSibling);
  }
  while (node instanceof Element && EMPHASIS.test(node.tagName)) {
    node = firstContent(node.firstChild);
  }
  // A search link (productSearchLink) stands in for a product with no page - no card to pair.
  return node instanceof Element && node.tagName === 'A' && !node.classList.contains('md-search-link') ? node : null;
}

/** `node` or the first sibling after it that is not blank text or a bare "1." number. */
function firstContent(node: ChildNode | null): ChildNode | null {
  let current = node;
  while (current && current.nodeType === Node.TEXT_NODE &&
    (!current.textContent?.trim() || ORDINAL.test(current.textContent))) {
    current = current.nextSibling;
  }
  return current;
}

/** A bold run holding nothing but a number - the `**1.**` the agent ranks its shortlist with. */
function isOrdinal(node: ChildNode | null): boolean {
  return node instanceof Element && EMPHASIS.test(node.tagName) && !node.children.length &&
    ORDINAL.test(node.textContent ?? '');
}

/**
 * Rebuilds one entry block as its name line and the details under it.
 *
 * The name line runs to the first line break or nested block; everything after is the detail.
 * A list item's position becomes a plain "2." on the name line, so a split shortlist keeps its
 * numbering without an `<ol>` wrapper indenting the entry. A short bold run after the name is
 * marked as the entry's badge, and the `recommended` entry gets the "Recommended" badge.
 */
function buildEntry(block: Element, ordinal: number | null, recommended: boolean): string {
  const name = document.createElement('p');
  name.className = 'md-entry-name';
  const details = document.createElement('p');
  const blocks: Node[] = [];

  let inName = true;
  for (const child of Array.from(block.childNodes)) {
    if (inName && child.nodeName === 'BR') {
      inName = false;
      continue;
    }
    if (child instanceof Element && BLOCK.test(child.tagName)) {
      inName = false;
      blocks.push(child.cloneNode(true));
      continue;
    }
    if (blocks.length && !(child.nodeType === Node.TEXT_NODE && !child.textContent?.trim())) {
      // Text after a nested block (rare) keeps its place after it rather than jumping above it.
      const tail = document.createElement('p');
      tail.appendChild(child.cloneNode(true));
      blocks.push(tail);
      continue;
    }
    (inName ? name : details).appendChild(child.cloneNode(true));
  }

  if (ordinal !== null && !/^\s*\d+[.)]/.test(name.textContent ?? '')) {
    name.insertBefore(document.createTextNode(`${ordinal}. `), name.firstChild);
  }

  // After the name, a short bold run ("**Best value**") is a badge and a dash or colon before
  // it only separates the two, so it is dropped rather than left dangling. Anything else is prose
  // the agent ran on from the name ("[BORDEAUX](url) is $95.00 ex GST...") and moves down into
  // the details, so it is not set in the name line's heading weight.
  const nameNodes = Array.from(name.childNodes);
  const linkAt = nameNodes.findIndex((child) => child instanceof Element && holdsLink(child));
  const runOn: ChildNode[] = [];
  for (const child of linkAt < 0 ? [] : nameNodes.slice(linkAt + 1)) {
    if (runOn.length) {
      runOn.push(child);
    } else if (child.nodeType === Node.TEXT_NODE && SEPARATOR.test(child.textContent ?? '')) {
      child.remove();
    } else if (isBadge(child)) {
      (child as Element).classList.add('md-entry-badge');
    } else {
      runOn.push(child);
    }
  }
  if (runOn.length) {
    const opening = runOn[0];
    if (opening.nodeType === Node.TEXT_NODE) {
      opening.textContent = (opening.textContent ?? '').replace(LEADING_SEPARATOR, '');
    }
    const lead = details.firstChild;
    runOn.forEach((child) => details.insertBefore(child, lead));
    if (lead) {
      details.insertBefore(document.createElement('br'), lead);
    }
  }

  // The frame's flag, not the reply text, says which product is recommended.
  if (recommended && !Array.from(name.querySelectorAll('.md-entry-badge'))
    .some((badge) => /^recommended$/i.test((badge.textContent ?? '').trim()))) {
    const badge = document.createElement('strong');
    badge.className = 'md-entry-badge';
    badge.textContent = 'Recommended';
    name.appendChild(badge);
  }

  trimEdges(details);
  return name.outerHTML + (details.childNodes.length ? details.outerHTML : '') +
    blocks.map((node) => (node as Element).outerHTML).join('');
}

/** Text that only joins a name to its badge or closes it - spaces, dashes, a colon, a stop or a bullet. */
const SEPARATOR = /^[\s\u2010-\u2015:;.,|\u00b7\u2022-]*$/;
const LEADING_SEPARATOR = /^[\s\u2010-\u2015:;,|\u00b7\u2022-]+/;

function holdsLink(element: Element): boolean {
  return element.tagName === 'A' || !!element.querySelector('a');
}

/** A short bold run with no link - "**Best value**" - shown as a tag. */
function isBadge(node: ChildNode): boolean {
  const text = (node.textContent ?? '').trim();
  return node instanceof Element && EMPHASIS.test(node.tagName) && !holdsLink(node) &&
    text.length > 0 && text.length <= 32;
}

/** Drops leading/trailing blank text and line breaks, which would open an empty first line. */
function trimEdges(element: Element): void {
  const isBlank = (node: ChildNode | null) => !!node &&
    (node.nodeName === 'BR' || (node.nodeType === Node.TEXT_NODE && !node.textContent?.trim()));
  while (isBlank(element.firstChild)) {
    element.firstChild!.remove();
  }
  while (isBlank(element.lastChild)) {
    element.lastChild!.remove();
  }
}

/** A copy of `list` holding only `items`, numbered from the first item's original position. */
function listOf(list: Element, items: Element[], position: number): string {
  const copy = list.cloneNode(false) as Element;
  if (list.tagName === 'OL') {
    const start = (parseInt(list.getAttribute('start') ?? '1', 10) || 1) + position;
    if (start !== 1) {
      copy.setAttribute('start', String(start));
    }
  }
  items.forEach((item) => copy.appendChild(item.cloneNode(true)));
  return copy.outerHTML;
}

function escapeText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
